if (!customElements.get('coaching-application')) {
  customElements.define(
    'coaching-application',
    class CoachingApplication extends HTMLElement {
      static STORAGE_KEY = 'coaching-application-progress-v2';

      connectedCallback() {
        this.form = this.closest('form');
        this.steps = Array.from(this.querySelectorAll('.coaching-app__step'));
        this.totalSteps = this.steps.length;
        this.progressBar = this.closest('.coaching-app')?.querySelector('.coaching-app__progress-bar');
        this.stepCount = this.closest('.coaching-app')?.querySelector('.coaching-app__step-count');
        this.liveRegion = this.querySelector('[data-live-region]');
        this.prevButton = this.querySelector('.coaching-app__nav-prev');
        this.primaryButton = this.querySelector('.coaching-app__nav-primary');
        this.currentStep = 1;

        this.restore();
        this.bindEvents();
        this.updateGoalConfirm();
        this.renderStep(this.currentStep, { skipFocus: true });
      }

      bindEvents() {
        this.prevButton?.addEventListener('click', () => this.goPrev());
        this.primaryButton?.addEventListener('click', (event) => this.handlePrimaryClick(event));

        this.addEventListener('input', () => this.persist());
        this.addEventListener('change', (event) => {
          const choice = event.target.closest('.coaching-app__choice');
          if (choice) this.updateChoiceState(choice.closest('.coaching-app__choices'));
          if (event.target.name === 'goal') this.updateGoalConfirm();
          this.persist();
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          const target = event.target;
          if (target.tagName === 'TEXTAREA') return;
          if (target.closest('.coaching-app__nav')) return;
          event.preventDefault();
          if (this.currentStep < this.totalSteps) {
            this.goNext();
          }
        });

        this.form?.addEventListener('submit', () => this.handleSubmit());
      }

      updateChoiceState(choicesWrapper) {
        if (!choicesWrapper) return;
        choicesWrapper.querySelectorAll('.coaching-app__choice').forEach((card) => {
          const input = card.querySelector('.coaching-app__choice-input');
          card.classList.toggle('is-selected', !!input?.checked);
        });
      }

      // Light "perceived personalization" touch: once a goal is chosen on
      // step 1, step 2 confirms it back before asking the next question,
      // rather than modifying the question's own grammar (which reads
      // awkwardly for some goal names, e.g. "your Lose Fat experience").
      updateGoalConfirm() {
        const confirmEl = this.querySelector('[data-goal-confirm]');
        if (!confirmEl) return;
        const goal = this.fieldValue('goal');
        if (!goal) {
          confirmEl.hidden = true;
          return;
        }
        confirmEl.textContent = `Great choice — let's tailor this for ${goal}.`;
        confirmEl.hidden = false;
      }

      currentStepEl() {
        return this.steps[this.currentStep - 1];
      }

      goNext() {
        const stepEl = this.currentStepEl();
        if (!this.validateStep(stepEl)) return;

        this.currentStep = Math.min(this.currentStep + 1, this.totalSteps);
        this.renderStep(this.currentStep);
        this.persist();
      }

      goPrev() {
        this.currentStep = Math.max(this.currentStep - 1, 1);
        this.renderStep(this.currentStep);
        this.persist();
      }

      handlePrimaryClick(event) {
        if (this.currentStep === this.totalSteps) return; // real submit button, let it submit
        event.preventDefault();
        this.goNext();
      }

      renderStep(stepNumber, { skipFocus = false } = {}) {
        this.steps.forEach((step, index) => {
          const isActive = index + 1 === stepNumber;
          step.classList.toggle('is-active', isActive);
          step.toggleAttribute('hidden', !isActive);
          step.classList.remove('has-error');
        });

        const percent = Math.round((stepNumber / this.totalSteps) * 100);
        if (this.progressBar) this.progressBar.style.width = `${percent}%`;
        if (this.stepCount) this.stepCount.textContent = `Step ${stepNumber} of ${this.totalSteps}`;

        if (this.prevButton) this.prevButton.hidden = stepNumber === 1;

        if (this.primaryButton) {
          if (stepNumber === this.totalSteps) {
            this.primaryButton.setAttribute('type', 'submit');
            this.primaryButton.textContent = this.primaryButton.dataset.submitLabel || 'Submit';
          } else {
            this.primaryButton.setAttribute('type', 'button');
            this.primaryButton.textContent = this.primaryButton.dataset.nextLabel || 'Continue';
          }
        }

        if (!skipFocus) {
          const heading = this.currentStepEl()?.querySelector('.coaching-app__step-label');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({ preventScroll: true });
        }

        if (this.liveRegion) {
          this.liveRegion.textContent = `Step ${stepNumber} of ${this.totalSteps}`;
        }
      }

      validateStep(stepEl) {
        if (!stepEl) return true;
        stepEl.classList.remove('has-error');

        const requiredInputs = Array.from(
          stepEl.querySelectorAll('input[required], textarea[required], select[required]')
        );
        const radioGroups = new Set();
        let firstInvalid = null;

        for (const input of requiredInputs) {
          if (input.type === 'radio') {
            radioGroups.add(input.name);
            continue;
          }
          if (!input.checkValidity()) {
            firstInvalid = firstInvalid || input;
          }
        }

        for (const name of radioGroups) {
          const groupInputs = stepEl.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`);
          const checked = Array.from(groupInputs).some((input) => input.checked);
          if (!checked) {
            firstInvalid = firstInvalid || groupInputs[0];
          }
        }

        if (firstInvalid) {
          stepEl.classList.add('has-error');
          firstInvalid.focus({ preventScroll: true });
          return false;
        }

        return true;
      }

      fieldValue(name) {
        const field = this.querySelector(`[name="${CSS.escape(name)}"]`);
        if (!field) return '';
        if (field.type === 'radio') {
          const checked = this.querySelector(`[name="${CSS.escape(name)}"]:checked`);
          return checked ? this.labelFor(checked) : '';
        }
        return field.value?.trim() || '';
      }

      labelFor(radioInput) {
        const card = radioInput.closest('.coaching-app__choice');
        return card?.querySelector('.coaching-app__choice-label')?.textContent.trim() || radioInput.value;
      }

      handleSubmit() {
        // Immediate visual feedback while the native submit/navigation is in
        // flight - doesn't delay or intercept the real submission (no
        // preventDefault here), just sets expectations and blocks a second
        // click on a slow connection.
        if (this.primaryButton) {
          this.primaryButton.disabled = true;
          this.primaryButton.textContent = 'Submitting…';
        }

        const first = this.fieldValue('first_name');
        const last = this.fieldValue('last_name');
        const nameField = this.form.querySelector('input[name="contact[name]"]');
        if (nameField) nameField.value = `${first} ${last}`.trim();

        const summaryLines = [
          'FIND YOUR PLAN APPLICATION',
          '----------------------------------------',
          `Name: ${first} ${last}`.trim(),
          `Email: ${this.fieldValue('contact[email]')}`,
          `Phone: ${this.fieldValue('contact[phone]')}`,
          '',
          `Primary Goal: ${this.fieldValue('goal')}`,
          '',
          `Training Experience: ${this.fieldValue('experience')}`,
        ];

        const bodyField = this.form.querySelector('textarea[name="contact[body]"]');
        if (bodyField) bodyField.value = summaryLines.join('\n');

        this.applyGoalRedirect();

        try {
          localStorage.removeItem(CoachingApplication.STORAGE_KEY);
        } catch (error) {
          /* localStorage unavailable, nothing to clean up */
        }
      }

      // Sets return_to to the goal-specific program product before the real
      // native submit, using the live goal_key -> product URL map rendered
      // by Liquid from the actual coaching_program metaobject entries. Never
      // fakes a successful submission - this only decides where Shopify
      // redirects the browser AFTER it genuinely processes the contact form.
      applyGoalRedirect() {
        const returnToField = this.form?.querySelector('input[name="return_to"]');
        if (!returnToField) return;

        const mapScript = document.querySelector('[data-goal-product-map]');
        let goalMap = {};
        try {
          goalMap = mapScript ? JSON.parse(mapScript.textContent) : {};
        } catch (error) {
          goalMap = {};
        }

        const selectedGoal = this.fieldValue('goal');
        const goalKey = selectedGoal.toLowerCase().trim().replace(/\s+/g, '-');
        const productUrl = goalMap[goalKey];

        if (productUrl) {
          returnToField.value = productUrl;
        }
        // else: leave the fallback value already set in the Liquid markup
        // (/collections/programs - the existing, always-live collection used
        // by the header nav) - covers a goal with no linked/published
        // product yet, or the map failing to load. The COACHING PROGRAMS
        // smart collection is deliberately NOT used as the fallback: it
        // isn't published to any sales channel itself yet, and 404s.
      }

      persist() {
        try {
          const data = {};
          this.querySelectorAll('input, textarea, select').forEach((field) => {
            if (!field.name) return;
            if (field.type === 'radio') {
              if (field.checked) data[field.name] = field.value;
            } else {
              data[field.name] = field.value;
            }
          });
          localStorage.setItem(
            CoachingApplication.STORAGE_KEY,
            JSON.stringify({ step: this.currentStep, values: data })
          );
        } catch (error) {
          /* localStorage unavailable (private mode, etc.) - progress just won't persist */
        }
      }

      restore() {
        let saved = null;
        try {
          const raw = localStorage.getItem(CoachingApplication.STORAGE_KEY);
          if (raw) saved = JSON.parse(raw);
        } catch (error) {
          saved = null;
        }

        if (!saved || !saved.values) return;

        Object.entries(saved.values).forEach(([name, value]) => {
          const fields = this.querySelectorAll(`[name="${CSS.escape(name)}"]`);
          fields.forEach((field) => {
            if (field.type === 'radio') {
              field.checked = field.value === value;
            } else {
              field.value = value;
            }
          });
        });

        this.querySelectorAll('.coaching-app__choices').forEach((wrapper) => this.updateChoiceState(wrapper));

        if (typeof saved.step === 'number' && saved.step >= 1 && saved.step <= this.totalSteps) {
          this.currentStep = saved.step;
        }
      }
    }
  );
}
